import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setConnected,
  setMicNotFound,
  setMicPermissionDenied,
  setRecording,
} from "@store/microphoneSlice";
import sleep from "@utils/sleep";
import { setMessage } from "@store/snackbarSlice";
import {
  VoiceDetectionEvent,
  VoiceDetector,
} from "@service/voice-detection/voice-detector";
import { VoiceDetectorFactory } from "@service/voice-detection/voice-detector.factory";
import { RootState } from "@store/store";
import { useTranslation } from "react-i18next";
import { MicStream } from "@service/mic-stream";
import { VoiceInputMode } from "@models/conversation-settings";
import { useCompanionConversation } from "@components/Companion/CompanionConversationContext";
import { debugCompanionPacing } from "@utils/companion-pacing-debug";

const MIC_CHECK_INTERVAL = 1000;

export const useRecordVoice = (
  processing: boolean,
  onVoiceRecorded: (blob: Blob | ArrayBufferLike) => void,
  streaming: boolean,
  voiceInputMode: VoiceInputMode,
  pauseDurationMs: number,
  onPushToTalkCaptureEnd?: () => void,
  onPushToTalkCaptureAbort?: () => void,
) => {
  const useStreamChunks =
    streaming &&
    (voiceInputMode === "vad" || voiceInputMode === "push_to_talk");
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const micStream = useRef<MicStream | null>(null);
  const mediaStream = useRef<MediaStream | null>(null);
  const voiceDetector = useRef<VoiceDetector | null>(null);
  const chunks = useRef<Blob[]>([]);
  const dispatch = useDispatch();
  const audioInputDeviceId =
    useSelector((state: RootState) => state.avatar.audioInputDeviceId) ?? "";
  const processingRef = useRef<boolean>(processing);
  // Keep in sync during render so sibling effects (e.g. PTT auto-start in CompanionToolbar)
  // see the same Redux `processing` as this hook before passive effects run.
  processingRef.current = processing;
  const { t } = useTranslation();
  const onVoiceRecordedRef = useRef(onVoiceRecorded);
  onVoiceRecordedRef.current = onVoiceRecorded;
  const onPushToTalkCaptureEndRef = useRef(onPushToTalkCaptureEnd);
  onPushToTalkCaptureEndRef.current = onPushToTalkCaptureEnd;
  const onPushToTalkCaptureAbortRef = useRef(onPushToTalkCaptureAbort);
  onPushToTalkCaptureAbortRef.current = onPushToTalkCaptureAbort;
  const {
    canStartUserCaptureLive,
    registerMicBridge,
    unregisterMicBridge,
    vadBargeInEnabled,
    runPttInterrupt,
  } = useCompanionConversation();
  const talking = useSelector((state: RootState) => state.avatar.talking);
  const talkingRef = useRef(talking);
  talkingRef.current = talking;
  const muted = useSelector((state: RootState) => state.avatar.muted);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const startRecording = useCallback((): void => {
    // Honors the mic-mute toggle wired to the toolbar; silently short-circuits
    // PTT starts, VAD `start` events, and post-interrupt auto-resume alike.
    if (mutedRef.current) {
      return;
    }
    if (micStream.current) {
      micStream.current.start();
      dispatch(setRecording(true));
    }
    const rec = mediaRecorder.current;
    if (!rec) {
      return;
    }
    if (rec.state === "inactive") {
      rec.start();
      dispatch(setRecording(true));
      return;
    }
    // After stop(), some browsers leave the recorder briefly non-inactive until onstop runs;
    // PTT auto-start can fire in the same turn as the prior send finishing.
    if (voiceInputMode === "push_to_talk") {
      queueMicrotask(() => {
        const r = mediaRecorder.current;
        if (r?.state === "inactive") {
          r.start();
          dispatch(setRecording(true));
        }
      });
    }
  }, [dispatch, voiceInputMode]);

  const stopRecording = useCallback((): void => {
    if (micStream.current) {
      micStream.current.stop();
      dispatch(setRecording(false));
      if (streaming && voiceInputMode === "push_to_talk") {
        queueMicrotask(() => {
          onPushToTalkCaptureEndRef.current?.();
        });
      }
    }
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.stop();
      dispatch(setRecording(false));
    }
  }, [dispatch, streaming, voiceInputMode]);

  const abortRecording = useCallback((): void => {
    // Stops PTT capture WITHOUT triggering end-talk semantics. Used when an
    // external event (e.g. gallery slide) supersedes the user's in-flight
    // utterance: we must drop the audio both client- and server-side instead
    // of finalizing it into an LLM turn that would race the new intro.
    if (micStream.current) {
      micStream.current.stop();
      dispatch(setRecording(false));
      if (streaming && voiceInputMode === "push_to_talk") {
        queueMicrotask(() => {
          onPushToTalkCaptureAbortRef.current?.();
        });
      }
    }
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.stop();
      dispatch(setRecording(false));
    }
  }, [dispatch, streaming, voiceInputMode]);

  useEffect(() => {
    if (
      processing &&
      voiceInputMode === "vad" &&
      !vadBargeInEnabled
    ) {
      stopRecording();
    }
  }, [processing, stopRecording, vadBargeInEnabled, voiceInputMode]);

  useEffect(() => {
    // Cut the mic immediately when the user mutes. `stopRecording` also flushes
    // the PTT `endUserUtterance` so an in-flight user turn can still finalize.
    if (muted) {
      stopRecording();
    }
  }, [muted, stopRecording]);

  useEffect(() => {
    let cancelled = false;

    const cleanupPipeline = () => {
      if (mediaStream.current) {
        mediaStream.current.getAudioTracks().forEach((track) => track.stop());
        mediaStream.current = null;
      }
      if (voiceDetector.current) {
        voiceDetector.current.destroy();
        voiceDetector.current = null;
      }
      if (micStream.current) {
        void micStream.current.destroy();
        micStream.current = null;
      }
      mediaRecorder.current = null;
      dispatch(setRecording(false));
      dispatch(setConnected(false));
    };

    const getAudioStream = async (
      deviceId: string,
    ): Promise<MediaStream | null> => {
      // Echo cancellation is key for VAD barge-in: without it, the assistant's
      // own audio bleeds into the mic and Silero triggers `onSpeechStart` the
      // moment the avatar starts talking.
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      };
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return null;
        }
        dispatch(setMicNotFound(false));
        dispatch(setMicPermissionDenied(false));
        return stream;
      } catch (e) {
        console.error(e);
        switch ((e as { name?: string })?.name) {
          case "NotFoundError": {
            dispatch(setMicNotFound(true));
            break;
          }
          case "NotAllowedError": {
            dispatch(setMicPermissionDenied(true));
            break;
          }
          default: {
            dispatch(
              setMessage({ content: t("errors.unknown"), severity: "error" }),
            );
            return null;
          }
        }
        await sleep(MIC_CHECK_INTERVAL);
        if (cancelled) {
          return null;
        }
        return getAudioStream(deviceId);
      }
    };

    const handleVoiceDetectionEvent = ({ type }: VoiceDetectionEvent) => {
      if (voiceInputMode !== "vad") {
        return;
      }
      // Muting the mic should also silence VAD: otherwise every echo-triggered
      // `start`/`real-start` would still barge in on the avatar.
      if (mutedRef.current) {
        return;
      }
      if (type === "start") {
        // Low-latency mic capture while assistant may still be talking; the
        // barge-in interrupt itself is deferred to `real-start` so VAD misfires
        // (e.g. speaker echo at the start of an avatar reply) don't cut it.
        if (canStartUserCaptureLive()) {
          startRecording();
        }
        return;
      }
      if (type === "real-start") {
        if (
          vadBargeInEnabled &&
          (processingRef.current || talkingRef.current)
        ) {
          void (async () => {
            await runPttInterrupt();
            if (canStartUserCaptureLive()) {
              startRecording();
            }
          })();
        }
        return;
      }
      if (type === "misfire") {
        // Roll back the `start`-triggered capture so we don't keep streaming
        // noise to the server while no real utterance ever landed.
        stopRecording();
        return;
      }
      stopRecording();
    };

    const initMediaRecorder = (stream: MediaStream): MediaRecorder => {
      const recorder = new MediaRecorder(stream);

      recorder.ondataavailable = (e) => {
        chunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks.current, { type: "audio/wav" });
        onVoiceRecordedRef.current(audioBlob);
        chunks.current = [];
      };

      return recorder;
    };

    const run = async () => {
      cleanupPipeline();
      const stream = await getAudioStream(audioInputDeviceId);
      if (cancelled || !stream) {
        return;
      }
      mediaStream.current = stream;
      if (useStreamChunks) {
        micStream.current = new MicStream(stream, (blob) =>
          onVoiceRecordedRef.current(blob),
        );
      } else {
        mediaRecorder.current = initMediaRecorder(stream);
      }

      if (voiceInputMode === "vad") {
        voiceDetector.current = VoiceDetectorFactory.create({
          stream,
          callback: handleVoiceDetectionEvent,
          pauseDuration: pauseDurationMs,
        });
      }
      dispatch(setConnected(true));
      debugCompanionPacing("mic pipeline ready", {
        voiceInputMode,
        pauseDurationMs,
        useStreamChunks,
        vadEnabled: voiceInputMode === "vad",
      });
    };

    void run();

    return () => {
      cancelled = true;
      cleanupPipeline();
    };
  }, [
    audioInputDeviceId,
    canStartUserCaptureLive,
    dispatch,
    pauseDurationMs,
    runPttInterrupt,
    startRecording,
    stopRecording,
    useStreamChunks,
    t,
    vadBargeInEnabled,
    voiceInputMode,
  ]);

  useEffect(() => {
    registerMicBridge({
      start: () => {
        if (canStartUserCaptureLive()) {
          startRecording();
        }
      },
      stop: () => {
        stopRecording();
      },
      abort: () => {
        abortRecording();
      },
    });
    return () => {
      unregisterMicBridge();
    };
  }, [
    abortRecording,
    canStartUserCaptureLive,
    registerMicBridge,
    startRecording,
    stopRecording,
    unregisterMicBridge,
  ]);

  return { startRecording, stopRecording, abortRecording };
};
